import { z } from 'zod';
import { PaymentTermsGroup } from '../../constants/payment-terms-group.js';
import { PriceAmountType } from '../../constants/price-amount-type.js';
import { PriceDerivationOperation } from '../../constants/price-derivation-operation.js';
import { optionalNullableDateInput, optionalUuidString, requiredUuidString, } from '../zod-helpers/index.js';
import { refinePriceRuleAmounts } from './amounts-refine.js';
import { INVERTED_WINDOW_MESSAGE, PRICE_RULE_CONSTRAINTS } from './constraints.js';
import { refinePriceRuleAmountDerivations } from './derivation-refine.js';
import { derivePriceRuleFields } from './derive-price-rule-fields.js';
import { refinePriceRuleScope } from './scope-refine.js';
// One row of SetPriceInput.amounts, matching PriceRuleAmountInput's GraphQL shape
// (packages/shared/src/generated/mutation-inputs.ts once regenerated). `amount: null` clears
// that one (paymentTermsId, paymentTermsGroupId, amountTypeId) cell -- see
// apply-price-rule-amounts.ts.
//
// The five derived* fields persist the List/MSRP formula builder's
// basis/operation/value -- all nullish, and per applyPriceRuleAmounts's contract, ABSENT MEANS
// NULL: a caller that omits them clears any existing formula on the cell. Cross-field rules
// (all-or-nothing, basis term/group shape, no self-derivation) live in derivation-refine.ts,
// wired into setPriceInputSchema's own superRefine below alongside refinePriceRuleAmounts.
export const priceRuleAmountInputSchema = z.object({
    paymentTermsId: optionalUuidString(`Invalid ${PRICE_RULE_CONSTRAINTS.paymentTermsId.label.toLowerCase()} selection.`).nullish(),
    paymentTermsGroupId: z
        .enum(PaymentTermsGroup, 'Invalid payment terms group selection.')
        .nullish(),
    amountTypeId: z.enum(PriceAmountType, 'Invalid amount type selection.'),
    amount: z
        .number()
        .positive(`${PRICE_RULE_CONSTRAINTS.amount.label} must be greater than 0.`)
        .max(PRICE_RULE_CONSTRAINTS.amount.max, `${PRICE_RULE_CONSTRAINTS.amount.label} must be ${PRICE_RULE_CONSTRAINTS.amount.max.toLocaleString()} or less.`)
        .nullable(),
    derivedFromAmountTypeId: z.enum(PriceAmountType, 'Invalid formula basis amount type.').nullish(),
    derivedFromPaymentTermsId: optionalUuidString('Invalid formula basis payment terms.').nullish(),
    derivedFromPaymentTermsGroupId: z
        .enum(PaymentTermsGroup, 'Invalid formula basis payment terms group.')
        .nullish(),
    derivationOperationId: z.enum(PriceDerivationOperation, 'Invalid formula operation.').nullish(),
    derivationValue: z
        .number()
        .nonnegative(`${PRICE_RULE_CONSTRAINTS.derivationValue.label} must be zero or greater.`)
        .max(PRICE_RULE_CONSTRAINTS.derivationValue.max, `${PRICE_RULE_CONSTRAINTS.derivationValue.label} must be ${PRICE_RULE_CONSTRAINTS.derivationValue.max.toLocaleString()} or less.`)
        .nullish(),
});
// scopeTypeId, priority, and pricingModeId are all derived (derive-price-rule-fields.ts) rather
// than user-supplied -- see revision doc §8. priceRuleId is optional: the sheet always has it
// (an unambiguous update-by-id), so price-rule-scope-key.ts's natural-key lookup is only reached
// by a caller without one.
export const setPriceInputSchema = z
    .object({
    priceRuleId: optionalUuidString('Invalid pricing rule selection.').nullish(),
    brandId: requiredUuidString(`${PRICE_RULE_CONSTRAINTS.brandId.label} is required.`, `Invalid ${PRICE_RULE_CONSTRAINTS.brandId.label.toLowerCase()} selection.`),
    productTreeId: requiredUuidString(`${PRICE_RULE_CONSTRAINTS.productTreeId.label} is required.`, `Invalid ${PRICE_RULE_CONSTRAINTS.productTreeId.label.toLowerCase()} selection.`),
    stateId: optionalUuidString(`Invalid ${PRICE_RULE_CONSTRAINTS.stateId.label.toLowerCase()} selection.`).nullish(),
    brandMarketConfigId: optionalUuidString(`Invalid ${PRICE_RULE_CONSTRAINTS.brandMarketConfigId.label.toLowerCase()} selection.`).nullish(),
    customerId: optionalUuidString(`Invalid ${PRICE_RULE_CONSTRAINTS.customerId.label.toLowerCase()} selection.`).nullish(),
    productId: optionalUuidString(`Invalid ${PRICE_RULE_CONSTRAINTS.productId.label.toLowerCase()} selection.`).nullish(),
    locked: z.boolean().nullish(),
    effectiveStart: optionalNullableDateInput,
    effectiveEnd: optionalNullableDateInput,
    amounts: z
        .array(priceRuleAmountInputSchema)
        .min(1, 'At least one amount is required.')
        .max(300, 'Too many amounts in a single call -- submit in smaller batches.'),
})
    .superRefine((value, ctx) => {
    const { scopeTypeId } = derivePriceRuleFields(value);
    refinePriceRuleScope({ scopeTypeId, customerId: value.customerId, productId: value.productId }, ctx);
    refinePriceRuleAmounts({ amounts: value.amounts }, ctx);
    refinePriceRuleAmountDerivations({ amounts: value.amounts }, ctx);
    // WHY: an empty or inverted window either raises a raw 500 (Postgres rejects effectiveEnd < effectiveStart) or silently writes an invisible zero-width active rule (effectiveEnd === effectiveStart) -- both must fail here instead.
    if (value.effectiveStart && value.effectiveEnd && value.effectiveEnd <= value.effectiveStart) {
        ctx.addIssue({
            code: 'custom',
            message: INVERTED_WINDOW_MESSAGE,
            path: ['effectiveEnd'],
        });
    }
});
//# sourceMappingURL=set-price-input.js.map